import { EffectsModule } from '@ngrx/effects';
import { NgModule, ApplicationRef, ErrorHandler } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { ROUTES } from './app.routes';
import { AppComponent } from './app.component';
import { InternalModule } from './internal';
import { PublicModule } from './public';
import { clearState, reducers } from './app.reducers';
import { AuthModule } from './shared/auth';
import { environment } from '../environments/environment';
import { VersioningService } from './shared';
import { LocationsEffects } from './internal/component/location-store/locations.effects';
import { GlobalEffects } from './shared/redux/global/global.effects';
import { FilterSettingsEffects } from './internal/shared/filter-settings-store/filter-settings.effects';
import { ToastrModule } from 'ngx-toastr';
import { IntercomModule } from './ng-intercom';
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import { NewRelicErrorHandler } from './shared/services/newrelic/newrelic-error-handler';

// analytics variable to call SEGMENT ANALYTICS throughout the app.
declare global {
  interface Window {
    analytics: any;
    newRelicBrowserAgent: any;
  }
}

export function provideErrorHandler() {
  if (environment.production || environment.staging) {
    return new NewRelicErrorHandler();
  } else {
    return new ErrorHandler();
  }
}

const PROVIDERS = [
  VersioningService
];

@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    AppComponent
  ],
  exports: [
    CommonModule
  ],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    InternalModule,
    PublicModule,
    RouterModule.forRoot(ROUTES, { useHash: true, scrollPositionRestoration: 'enabled', bindToComponentInputs: true }),
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-right',
    }),
    /**
     * StoreModule.forRoot is imported once in the root module, accepting a reducer
     * function or object map of reducer functions. If passed an object of
     * reducers, combineReducers will be run creating your application
     * meta-reducer. This returns all providers for an @ngrx/store
     * based application.
     */
    // NOTE: https://stackoverflow.com/questions/60464978/typeerror-cannot-assign-to-read-only-property-tview-of-object-object-object
    StoreModule.forRoot(reducers, {
      metaReducers: [clearState],
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      }
    }),
    /**
     * Store devtools instrument the store retaining past versions of state
     * and recalculating new states. This enables powerful time-travel
     * debugging.
     *
     * To use the debugger, install the Redux Devtools extension for either
     * Chrome or Firefox
     *
     * See: https://github.com/zalmoxisus/redux-devtools-extension
     *
     * Note that you must instrument after importing StoreModule (config is optional)
     */
    // // !environment.production ? StoreDevtoolsModule.instrument() : [],
    StoreDevtoolsModule.instrument({
      name: 'SmartProperty',
      maxAge: 25, //  Retains last 25 states
      connectInZone: true
    }),
    /**
         * @ngrx/router-store keeps router state up-to-date in the store.
         */
    StoreRouterConnectingModule.forRoot({
      stateKey: 'router',
      routerState: 1
    }),
    /**
     * EffectsModule.forRoot() is imported once in the root module and
     * sets up the effects class to be initialized immediately when the
     * application starts.
     *
     * See: https://github.com/ngrx/platform/blob/master/docs/effects/api.md#forroot
     */
    EffectsModule.forRoot([
      LocationsEffects,
      GlobalEffects,
      FilterSettingsEffects,
    ]),
    AuthModule.forRoot(),
    IntercomModule.forRoot({
      appId: 'gy1yf80a', // from your Intercom config
      updateOnRouterChange: true // will automatically run `update` on router event changes. Default: `false`
    })],

  providers: [
    PROVIDERS,
    { provide: ErrorHandler, useFactory: provideErrorHandler },
    provideHttpClient(withInterceptorsFromDi())
  ]
})

export class AppModule {
  constructor(public appRef: ApplicationRef) {

    if (environment.staging || environment.production) {

      // Initialize New Relic Browser agent    
      window.newRelicBrowserAgent = new BrowserAgent({
        init: {
          distributed_tracing: { enabled: true },
          privacy: { cookies_enabled: true },
          ajax: { deny_list: ['bam.nr-data.net'] },
          //session_trace: { enabled: true }
        },
        info: {
          beacon: 'bam.nr-data.net',
          errorBeacon: 'bam.nr-data.net',
          licenseKey: 'f8ed6fd573',
          applicationID: environment.newRelic.applicationID,
          sa: 1
        },
        loader_config: {
          accountID: '455277',
          trustKey: '455277',
          agentID: environment.newRelic.applicationID,
          licenseKey: 'f8ed6fd573',
          applicationID: environment.newRelic.applicationID
        }
      });

      //The agent loader code executes immediately on instantiation.
      window.newRelicBrowserAgent.start();
    }
  }
}
